import { useHistory, useParams } from 'react-router';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import useAuth from '../../hooks/utils/useAuth';
import {
  useContentByTopicInfinite,
  useContentByTopicListenerAudioInfinite,
  useContentByTopicResourcesInfinite,
  useContentByTopicPeersInfinite,
  useContentByTopicCollectionsQuery,
} from '../../hooks/api/useBrowseByTopics';
import { useSessionStorage } from '../../hooks/utils/useSessionStorage';
import { useTagGroups } from '../../hooks/api/useTagGroups';
import { Header } from '../../components/layout/header';
import { Footer } from '../../components/layout/footer';
import { SearchInput } from '../../components/search-input';
import { CollectionSummary } from '../../components/collection-summary';
import { DropdownPill } from '../../components/dropdown-pill';
import { VideoExperienceTile } from '../../components/video-experience-tile';
import { AudioExperienceTile } from '../../components/audio-experience-tile';
import { ResourceTile } from '../../components/resource-tile';
import { PeerAvatar } from '../../components/peer-avatar';
import { ContentSection } from '../../components/content-section';
import { HeroSection } from '../../components/hero-section';
import { Experience, Content, People } from '../../hooks/api/useExperiences';
import { InlineSpinner } from '../../components/common/spinner';
import { capitalizeWords } from '../../utils/constants';
import { getTopicCircle, slugToTagGroupKey } from '../../utils/topic-utils';
import { ROUTE_PATH } from '../../routes/route-paths';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { Features, useFeatureFlag } from '../../hooks/api/useFeatureFlag';
import { MatchStyleTrialDropdown } from '../../components/match-style-trial-dropdown';
import { MatchStyleVariant, useMatchStyleTrial } from '../../components/peer-listener-match-hint';

type ContentType = undefined | 'video' | 'audio' | 'resources' | 'peers';

const VIDEO_ITEMS = 6;
const AUDIO_ITEMS = 4;
const RESOURCE_ITEMS = 4;
const PEER_ITEMS = 6;
const FULL_PAGE_THIN_ITEMS = 24;
const FULL_PAGE_WIDE_ITEMS = 16;

const BrowseByTopic = () => {
  const history = useHistory();
  const { tag_group_key } = useParams<{ tag_group_key: string }>();
  const tagGroupKey = tag_group_key ? slugToTagGroupKey[tag_group_key] : undefined;
  const { user } = useAuth();
  const dfMdMedia = useMediaQuery('md');
  const clientId = useSessionStorage('clientId') || user?.caller_role?.active_subscription?.package?.client?.id;
  const [contentTypeFilter, setContentTypeFilter] = useState<ContentType>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const matchPreviewEnabled = useFeatureFlag(Features.SEARCH_MATCH_EXPLAINABILITY).enabled;
  const { matchStyle, setMatchStyle } = useMatchStyleTrial(matchPreviewEnabled);

  const { getTagGroupByKey } = useTagGroups(!!tagGroupKey);
  const selectedTagGroup = useMemo(() => {
    if (!tagGroupKey) return undefined;
    const tagGroupsByKey = getTagGroupByKey(tagGroupKey);
    return tagGroupsByKey.length > 0 ? tagGroupsByKey[0] : undefined;
  }, [tagGroupKey, getTagGroupByKey]);

  const tagGroupId = selectedTagGroup?.id;
  const hasSearch = Boolean(searchQuery?.trim());
  const activeMatchStyle = hasSearch ? matchStyle : 'none';

  // When searching: single Algolia request (combined content)
  const searchInfinite = useContentByTopicInfinite(tagGroupId, Number(clientId), searchQuery);

  // When not searching: separate fetch per content type (own pagination + loading state)
  const listenerAudioInfinite = useContentByTopicListenerAudioInfinite(tagGroupId, Number(clientId), searchQuery);
  const resourcesInfinite = useContentByTopicResourcesInfinite(tagGroupId, Number(clientId), searchQuery);
  const peersInfinite = useContentByTopicPeersInfinite(tagGroupId, Number(clientId), searchQuery);
  const collectionsQuery = useContentByTopicCollectionsQuery(tagGroupId, Number(clientId), searchQuery);

  const flattened = hasSearch
    ? searchInfinite.flattened
    : (() => {
        const audioPages = listenerAudioInfinite.data?.pages ?? [];
        const resourcePages = resourcesInfinite.data?.pages ?? [];
        const peerPages = peersInfinite.data?.pages ?? [];
        return {
          listener_audio: audioPages.flatMap((p) => p.data ?? []),
          client_resources: resourcePages.flatMap((p) => p.data?.client_resources ?? []),
          global_resources: resourcePages.flatMap((p) => p.data?.global_resources ?? []),
          listener_roles: peerPages.flatMap((p) => p.data ?? []),
          content_collections: collectionsQuery.data,
          listener_audio_total_count: audioPages[0]?.count ?? null,
          resources_total_count: resourcePages[0]?.count ?? null,
          listener_roles_total_count: peerPages[0]?.count ?? null,
        };
      })();

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const videoExperiences = useMemo(() => {
    if (!flattened?.listener_audio) return [];
    return flattened.listener_audio.filter((audio) => (audio as Content).video_id);
  }, [flattened?.listener_audio]);

  const audioExperiences = useMemo(() => {
    if (!flattened?.listener_audio) return [];
    return flattened.listener_audio.filter((audio) => !(audio as Content).video_id);
  }, [flattened?.listener_audio]);

  const allResources = useMemo(() => {
    if (!flattened) return [];
    return [...(flattened.client_resources || []), ...(flattened.global_resources || [])];
  }, [flattened]);

  const peers = useMemo(() => {
    if (!flattened?.listener_roles) return [];
    return flattened.listener_roles;
  }, [flattened?.listener_roles]);

  const listenerAudioTotalCount = flattened?.listener_audio_total_count ?? null;
  const resourcesTotalCount = flattened?.resources_total_count ?? null;
  const peersTotalCount = flattened?.listener_roles_total_count ?? null;

  const isLoadingContent = hasSearch
    ? searchInfinite.isLoading
    : listenerAudioInfinite.isLoading ||
      resourcesInfinite.isLoading ||
      peersInfinite.isLoading ||
      collectionsQuery.isLoading;

  const isFetchingNextPage = hasSearch
    ? searchInfinite.isFetchingNextPage
    : listenerAudioInfinite.isFetchingNextPage ||
      resourcesInfinite.isFetchingNextPage ||
      peersInfinite.isFetchingNextPage;

  const hasNextPage = hasSearch ? searchInfinite.hasNextPage : false;
  const fetchNextPage = useMemo(
    () => (hasSearch && searchInfinite.fetchNextPage ? searchInfinite.fetchNextPage : () => {}),
    [hasSearch, searchInfinite.fetchNextPage],
  );

  const hasNextPageListenerAudio = hasSearch ? false : (listenerAudioInfinite.hasNextPage ?? false);
  const hasNextPageResources = hasSearch ? false : (resourcesInfinite.hasNextPage ?? false);
  const hasNextPagePeers = hasSearch ? false : (peersInfinite.hasNextPage ?? false);

  const contentError = hasSearch
    ? searchInfinite.error
    : listenerAudioInfinite.error || resourcesInfinite.error || peersInfinite.error || collectionsQuery.error || null;

  // Get initial items count based on filter
  const getInitialItems = useMemo(() => {
    const hasFilter = contentTypeFilter !== undefined;
    return {
      video: hasFilter ? FULL_PAGE_THIN_ITEMS : VIDEO_ITEMS,
      audio: hasFilter ? FULL_PAGE_WIDE_ITEMS : AUDIO_ITEMS,
      resources: hasFilter ? FULL_PAGE_WIDE_ITEMS : RESOURCE_ITEMS,
      peers: hasFilter ? FULL_PAGE_THIN_ITEMS : PEER_ITEMS,
    };
  }, [contentTypeFilter]);

  const [itemsShown, setItemsShown] = useState({
    video: getInitialItems.video,
    audio: getInitialItems.audio,
    resources: getInitialItems.resources,
    peers: getInitialItems.peers,
  });

  // Reset itemsShown when filters change
  useEffect(() => {
    setItemsShown({
      video: getInitialItems.video,
      audio: getInitialItems.audio,
      resources: getInitialItems.resources,
      peers: getInitialItems.peers,
    });
  }, [contentTypeFilter, searchQuery, getInitialItems]);

  const fetchNextPageListenerAudio = useMemo(
    () => listenerAudioInfinite.fetchNextPage ?? (() => {}),
    [listenerAudioInfinite.fetchNextPage],
  );
  const fetchNextPageResources = useMemo(
    () => resourcesInfinite.fetchNextPage ?? (() => {}),
    [resourcesInfinite.fetchNextPage],
  );
  const fetchNextPagePeers = useMemo(() => peersInfinite.fetchNextPage ?? (() => {}), [peersInfinite.fetchNextPage]);

  const maybeFetchNextPage = useCallback(() => {
    if (hasSearch && hasNextPage && !isFetchingNextPage && fetchNextPage) fetchNextPage();
  }, [hasSearch, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const loadMoreVideos = useCallback(() => {
    if (itemsShown.video < videoExperiences.length) {
      const nextShown = Math.min(itemsShown.video + VIDEO_ITEMS, videoExperiences.length);
      setItemsShown((prev) => ({ ...prev, video: nextShown }));
      if (
        !hasSearch &&
        nextShown >= videoExperiences.length - VIDEO_ITEMS &&
        hasNextPageListenerAudio &&
        !listenerAudioInfinite.isFetchingNextPage
      ) {
        fetchNextPageListenerAudio();
      } else if (
        hasSearch &&
        nextShown >= videoExperiences.length - VIDEO_ITEMS &&
        hasNextPage &&
        !isFetchingNextPage &&
        fetchNextPage
      ) {
        fetchNextPage();
      }
    } else {
      if (!hasSearch && hasNextPageListenerAudio && !listenerAudioInfinite.isFetchingNextPage)
        fetchNextPageListenerAudio();
      else maybeFetchNextPage();
    }
  }, [
    itemsShown.video,
    videoExperiences.length,
    hasSearch,
    hasNextPageListenerAudio,
    listenerAudioInfinite.isFetchingNextPage,
    fetchNextPageListenerAudio,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    maybeFetchNextPage,
  ]);

  const loadMoreAudio = useCallback(() => {
    if (itemsShown.audio < audioExperiences.length) {
      const nextShown = Math.min(itemsShown.audio + AUDIO_ITEMS, audioExperiences.length);
      setItemsShown((prev) => ({ ...prev, audio: nextShown }));
      if (
        !hasSearch &&
        nextShown >= audioExperiences.length - AUDIO_ITEMS &&
        hasNextPageListenerAudio &&
        !listenerAudioInfinite.isFetchingNextPage
      ) {
        fetchNextPageListenerAudio();
      } else if (
        hasSearch &&
        nextShown >= audioExperiences.length - AUDIO_ITEMS &&
        hasNextPage &&
        !isFetchingNextPage &&
        fetchNextPage
      ) {
        fetchNextPage();
      }
    } else {
      if (!hasSearch && hasNextPageListenerAudio && !listenerAudioInfinite.isFetchingNextPage)
        fetchNextPageListenerAudio();
      else maybeFetchNextPage();
    }
  }, [
    itemsShown.audio,
    audioExperiences.length,
    hasSearch,
    hasNextPageListenerAudio,
    listenerAudioInfinite.isFetchingNextPage,
    fetchNextPageListenerAudio,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    maybeFetchNextPage,
  ]);

  const loadMoreResources = useCallback(() => {
    if (itemsShown.resources < allResources.length) {
      const nextShown = Math.min(itemsShown.resources + RESOURCE_ITEMS, allResources.length);
      setItemsShown((prev) => ({ ...prev, resources: nextShown }));
      if (
        !hasSearch &&
        nextShown >= allResources.length - RESOURCE_ITEMS &&
        hasNextPageResources &&
        !resourcesInfinite.isFetchingNextPage
      ) {
        fetchNextPageResources();
      } else if (
        hasSearch &&
        nextShown >= allResources.length - RESOURCE_ITEMS &&
        hasNextPage &&
        !isFetchingNextPage &&
        fetchNextPage
      ) {
        fetchNextPage();
      }
    } else {
      if (!hasSearch && hasNextPageResources && !resourcesInfinite.isFetchingNextPage) fetchNextPageResources();
      else maybeFetchNextPage();
    }
  }, [
    itemsShown.resources,
    allResources.length,
    hasSearch,
    hasNextPageResources,
    resourcesInfinite.isFetchingNextPage,
    fetchNextPageResources,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    maybeFetchNextPage,
  ]);

  const loadMorePeers = useCallback(() => {
    if (itemsShown.peers < peers.length) {
      const nextShown = Math.min(itemsShown.peers + PEER_ITEMS, peers.length);
      setItemsShown((prev) => ({ ...prev, peers: nextShown }));
      if (
        !hasSearch &&
        nextShown >= peers.length - PEER_ITEMS &&
        hasNextPagePeers &&
        !peersInfinite.isFetchingNextPage
      ) {
        fetchNextPagePeers();
      } else if (
        hasSearch &&
        nextShown >= peers.length - PEER_ITEMS &&
        hasNextPage &&
        !isFetchingNextPage &&
        fetchNextPage
      ) {
        fetchNextPage();
      }
    } else {
      if (!hasSearch && hasNextPagePeers && !peersInfinite.isFetchingNextPage) fetchNextPagePeers();
      else maybeFetchNextPage();
    }
  }, [
    itemsShown.peers,
    peers.length,
    hasSearch,
    hasNextPagePeers,
    peersInfinite.isFetchingNextPage,
    fetchNextPagePeers,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    maybeFetchNextPage,
  ]);

  const topicCollectionRef = useRef<any>(null);
  const topicCollection = useMemo(() => {
    const collections = hasSearch ? flattened?.content_collections : (collectionsQuery.data ?? []);
    if (collections && collections.length > 0) {
      topicCollectionRef.current = collections[0];
      return topicCollectionRef.current;
    }
    return topicCollectionRef.current;
  }, [hasSearch, flattened?.content_collections, collectionsQuery.data]);

  if (!tagGroupKey) {
    history.push(ROUTE_PATH.HOME);
  }

  if (!tagGroupId || !clientId) {
    return <InlineSpinner />;
  }

  const hasError = Boolean(contentError);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />
      <div className={`flex-1 ${!dfMdMedia ? 'pt-[76px]' : ''}`}>
        <HeroSection
          title={selectedTagGroup?.name ? capitalizeWords(selectedTagGroup.name) : 'Topic'}
          description={selectedTagGroup?.description}
          icon={selectedTagGroup ? getTopicCircle(selectedTagGroup) : undefined}
          iconAlt="Topic icon"
          padding="py-8 md:py-12"
        />
        {(topicCollection || isLoadingContent || collectionsQuery.isLoading) && (
          <div className="max-w-page mx-auto px-6 pt-8">
            <CollectionSummary
              collection={topicCollection}
              collectionName={`Featured Content on ${capitalizeWords(selectedTagGroup?.name || 'Topic')}`}
              isPrimaryLandingPage={false}
              selectedTagGroupName={selectedTagGroup?.name}
              isLoading={
                !hasSearch ? collectionsQuery.isLoading && !topicCollection : isLoadingContent && !topicCollection
              }
            />
          </div>
        )}
        <div className="max-w-page mx-auto space-y-4 px-6 pb-6 pt-8">
          <h2 className="font-albert-sans text-xl font-semibold leading-8 text-black md:text-[28px] md:leading-[60px]">
            Explore Stories and Resources on {selectedTagGroup?.name.toLowerCase()}
          </h2>
          <div className={`flex items-start justify-start gap-3 ${dfMdMedia ? 'flex-row' : 'flex-col'}`}>
            <SearchInput onSearch={handleSearch} initialValue={searchQuery} className="mb-0" />
            <DropdownPill
              label="Content type"
              placeholder="All Content"
              options={[
                { value: 'video', label: 'Video Stories' },
                { value: 'audio', label: 'Audio Stories' },
                { value: 'resources', label: 'Resources' },
                { value: 'peers', label: 'Peers' },
              ]}
              selectedValue={contentTypeFilter}
              onSelect={(value) => setContentTypeFilter(value as ContentType)}
            />
            {matchPreviewEnabled ? (
              <MatchStyleTrialDropdown selectedValue={matchStyle} onSelect={setMatchStyle} />
            ) : null}
          </div>
          {matchPreviewEnabled ? (
            <p className="font-open-sans text-sm text-gray-500">
              Match styles are a preview for your client. Search to see why each result matched.
            </p>
          ) : null}
        </div>
        <Body
          isLoading={isLoadingContent}
          isFetchingNextPage={isFetchingNextPage}
          hasError={hasError}
          contentTypeFilter={contentTypeFilter}
          videoExperiences={videoExperiences}
          audioExperiences={audioExperiences}
          allResources={allResources}
          peers={peers}
          itemsShown={itemsShown}
          loadMoreVideos={loadMoreVideos}
          loadMoreAudio={loadMoreAudio}
          loadMoreResources={loadMoreResources}
          loadMorePeers={loadMorePeers}
          listenerAudioTotalCount={listenerAudioTotalCount}
          resourcesTotalCount={resourcesTotalCount}
          peersTotalCount={peersTotalCount}
          hasNextPage={hasSearch ? hasNextPage : hasNextPageListenerAudio || hasNextPageResources || hasNextPagePeers}
          fetchNextPage={fetchNextPage}
          searchQuery={searchQuery}
          selectedTagGroupName={selectedTagGroup?.name}
          isMobile={!dfMdMedia}
          matchStyle={activeMatchStyle}
          sectionLoading={
            hasSearch
              ? undefined
              : {
                  video: listenerAudioInfinite.isLoading || listenerAudioInfinite.isFetchingNextPage,
                  audio: listenerAudioInfinite.isLoading || listenerAudioInfinite.isFetchingNextPage,
                  resources: resourcesInfinite.isLoading || resourcesInfinite.isFetchingNextPage,
                  peers: peersInfinite.isLoading || peersInfinite.isFetchingNextPage,
                }
          }
        />
      </div>
      <Footer />
    </div>
  );
};

interface BodyProps {
  isLoading: boolean;
  isFetchingNextPage?: boolean;
  hasError: boolean;
  contentTypeFilter: ContentType;
  videoExperiences: (Experience | Content)[];
  audioExperiences: (Experience | Content)[];
  allResources: any[];
  peers: People[];
  itemsShown: {
    video: number;
    audio: number;
    resources: number;
    peers: number;
  };
  loadMoreVideos: () => void;
  loadMoreAudio: () => void;
  loadMoreResources: () => void;
  loadMorePeers: () => void;
  listenerAudioTotalCount: number | null | undefined;
  resourcesTotalCount?: number | null;
  peersTotalCount?: number | null;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  searchQuery: string;
  selectedTagGroupName?: string;
  isMobile?: boolean;
  matchStyle?: MatchStyleVariant;
  /** When set, each section uses its own loading state instead of the global isLoading/isFetchingNextPage */
  sectionLoading?: {
    video: boolean;
    audio: boolean;
    resources: boolean;
    peers: boolean;
  };
}

const Body = ({
  isLoading,
  isFetchingNextPage = false,
  hasError,
  contentTypeFilter,
  videoExperiences,
  audioExperiences,
  allResources,
  peers,
  itemsShown,
  loadMoreVideos,
  loadMoreAudio,
  loadMoreResources,
  loadMorePeers,
  listenerAudioTotalCount,
  resourcesTotalCount,
  peersTotalCount,
  hasNextPage,
  fetchNextPage,
  searchQuery,
  selectedTagGroupName,
  isMobile = false,
  matchStyle = 'none',
  sectionLoading,
}: BodyProps) => {
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);

  const isLoadingVideo = sectionLoading?.video ?? (isLoading || isFetchingNextPage);
  const isLoadingAudio = sectionLoading?.audio ?? (isLoading || isFetchingNextPage);
  const isLoadingResources = sectionLoading?.resources ?? (isLoading || isFetchingNextPage);
  const isLoadingPeers = sectionLoading?.peers ?? (isLoading || isFetchingNextPage);

  useEffect(() => {
    if (!isMobile || !fetchNextPage || !hasNextPage || isFetchingNextPage) return;
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) fetchNextPage();
      },
      { root: null, rootMargin: '400px', threshold: 0.01 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isMobile, fetchNextPage, hasNextPage, isFetchingNextPage]);

  if (hasError) {
    return (
      <div className="max-w-page mx-auto px-6 py-8">
        <div className="text-center text-red-500">Error loading content. Please try again.</div>
      </div>
    );
  }

  const showVideo = contentTypeFilter === undefined || contentTypeFilter === 'video';
  const showAudio = contentTypeFilter === undefined || contentTypeFilter === 'audio';
  const showResources = contentTypeFilter === undefined || contentTypeFilter === 'resources';
  const showPeers = contentTypeFilter === undefined || contentTypeFilter === 'peers';

  return (
    <div className="max-w-page mx-auto px-6 py-8">
      <ContentSection
        title="Video Stories"
        items={videoExperiences}
        itemsShown={itemsShown.video}
        totalCount={listenerAudioTotalCount ?? undefined}
        onLoadMore={loadMoreVideos}
        shouldShow={showVideo && (videoExperiences.length > 0 || isLoadingVideo)}
        isLoading={isLoadingVideo}
        renderItem={(experience, index) => (
          <VideoExperienceTile
            key={experience?.id || `skeleton-${index}`}
            experience={experience as Experience | Content | undefined}
            handleScheduleModalOpen={() => {}}
            selectedTagGroupName={selectedTagGroupName}
            isLoading={!experience}
            matchStyle={matchStyle}
          />
        )}
      />
      <ContentSection
        title="Audio Stories"
        items={audioExperiences}
        itemsShown={itemsShown.audio}
        totalCount={listenerAudioTotalCount ?? undefined}
        onLoadMore={loadMoreAudio}
        shouldShow={showAudio && (audioExperiences.length > 0 || isLoadingAudio)}
        isLoading={isLoadingAudio}
        renderItem={(experience, index) => (
          <AudioExperienceTile
            key={experience?.id || `skeleton-${index}`}
            experience={experience as Experience | Content | undefined}
            selectedTagGroupName={selectedTagGroupName}
            isLoading={!experience}
            matchStyle={matchStyle}
          />
        )}
      />
      <ContentSection
        title="Resources"
        items={allResources}
        itemsShown={itemsShown.resources}
        totalCount={resourcesTotalCount ?? undefined}
        onLoadMore={loadMoreResources}
        shouldShow={showResources && (allResources.length > 0 || isLoadingResources)}
        isLoading={isLoadingResources}
        renderItem={(resource, index) => (
          <ResourceTile
            key={resource?.id || `skeleton-${index}`}
            resource={resource || undefined}
            id={resource?.id}
            image={resource?.image_url}
            resourceName={resource?.name}
            caption={resource?.description}
            isGlobal={resource ? !resource.client_id : undefined}
            tagGroupNames={(resource as any)?.tag_group_names}
            selectedTagGroupName={selectedTagGroupName}
            isLoading={!resource}
            matchStyle={matchStyle}
          />
        )}
      />
      <ContentSection
        title="Peers"
        items={peers}
        itemsShown={itemsShown.peers}
        totalCount={peersTotalCount ?? undefined}
        onLoadMore={loadMorePeers}
        shouldShow={showPeers && (peers.length > 0 || isLoadingPeers)}
        isLoading={isLoadingPeers}
        renderItem={(peer, index) => (
          <PeerAvatar
            key={peer?.listener_role_id || `skeleton-${index}`}
            peer={peer as People | undefined}
            isLoading={!peer}
            matchStyle={matchStyle}
          />
        )}
      />
      {((contentTypeFilter === undefined &&
        videoExperiences.length === 0 &&
        audioExperiences.length === 0 &&
        allResources.length === 0 &&
        peers.length === 0) ||
        (contentTypeFilter === 'video' && videoExperiences.length === 0) ||
        (contentTypeFilter === 'audio' && audioExperiences.length === 0) ||
        (contentTypeFilter === 'resources' && allResources.length === 0) ||
        (contentTypeFilter === 'peers' && peers.length === 0)) && (
        <div className="text-center text-gray-500">
          <p className="text-lg">No content found for this topic.</p>
          {searchQuery && <p className="mt-2 text-sm">Try adjusting your search.</p>}
        </div>
      )}
      {/* Mobile: load next page when user scrolls near bottom (viewport intersection) */}
      {isMobile && hasNextPage && <div ref={loadMoreSentinelRef} className="h-4 w-full" aria-hidden="true" />}
    </div>
  );
};

export default BrowseByTopic;
